import { CheckOutlined, CloseOutlined, RobotOutlined, SafetyOutlined, ToolOutlined } from '@ant-design/icons'
import type { ApprovalDecision, TurnProgressStage } from '@salescore/agent-contract'
import { Alert, Avatar, Button, Spin } from 'antd'
import { memo, type ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChatRow } from '../../domain/chatRows'
import { splitKpiPlan } from '../../domain/kpiPlan'
import type { ChatItem } from '../../domain/sessionChat'
import { type ToolActivity, toolActivityLabelOf } from '../../domain/toolActivity'
import {
  msUntilConnectingLabel,
  type ThinkingLabel,
  thinkingLabelOf,
  type TurnWaiting,
} from '../../domain/turnProgress'
import { useAgentChatActions } from '../../state/agentWidgetContext'
import { AgentMarkdown, keepFocusOnMouseDown } from './AgentMarkdown'
import { KpiPlanCard } from './KpiPlanCard'
import { toolActivityLabelsOf } from './toolActivityLabels'

// 会話の1行の表示。playground の ChatItemView から移した。変更のカードは #10116 で足す

// エージェントの発言の横に出すアイコン。見出しと同じロボットにする
const AgentAvatar = (): ReactElement => (
  <Avatar size="small" icon={<RobotOutlined />} className="shrink-0 bg-gray-100 text-gray-600" />
)

/**
 * ターンの実行中に、会話の最後に出す「考え中」の欄。実行中の印（スピナー）は、会話の中でこれだけに出す。
 * 文言は、runtime の progress で届いた段階か、最初のイベントを待つ間の「接続しています」か「考え中」にする（turnProgress.ts）。
 * 呼ぶ側は、ターンごとに key を変えて作り直す
 */
export const AgentThinkingIndicator = ({
  stage,
  waiting,
}: {
  stage: TurnProgressStage | null
  waiting: TurnWaiting | null
}): ReactElement => {
  const { t } = useTranslation()
  // 描くたびに時刻を読まないよう、表示を決める時刻を状態に持つ。送った時刻から始め、切り替える時刻が来たら進める
  const [now, setNow] = useState(() => (waiting === null ? 0 : Date.parse(waiting.sentAt)))
  const labels: Record<ThinkingLabel, string> = {
    accepted: t(`受け付けました`),
    preparing: t(`準備しています`),
    startingAgent: t(`エージェントを起動しています`),
    connectingTools: t(`SALESCORE に接続しています`),
    restoringHistory: t(`前の会話を読み込んでいます`),
    thinking: t(`考え中`),
    connecting: t(`接続しています`),
  }

  useEffect(() => {
    if (msUntilConnectingLabel(stage, waiting, now) === null) {
      return
    }
    // 送ってから描くまでに時間がたっていることがあるので、残りの時間は今の時刻で数え、もう過ぎていればすぐに進める
    const timer = setTimeout(
      () => {
        setNow(Date.now())
      },
      msUntilConnectingLabel(stage, waiting, Date.now()) ?? 0,
    )
    return (): void => {
      clearTimeout(timer)
    }
  }, [stage, waiting, now])

  return (
    <div className="flex gap-2" role="status">
      <AgentAvatar />
      <div className="flex items-center gap-2 text-gray-500" data-e2e="agent-thinking">
        <Spin size="small" />
        {labels[thinkingLabelOf(stage, waiting, now)]}
      </div>
    </div>
  )
}

// 終わり方の印。うまくいったかを一行から分かるようにする。実行中は印を出さず、実行中であることは会話の最後の「考え中」で示す
const ToolEndMark = ({ status }: { status: string | null }): ReactElement | null => {
  const { t } = useTranslation()
  switch (status) {
    case 'completed': {
      return <CheckOutlined aria-label={t(`完了`)} className="shrink-0 text-green-600" />
    }
    case 'failed': {
      return <CloseOutlined aria-label={t(`失敗`)} className="shrink-0 text-red-500" />
    }
    case 'interrupted': {
      return <CloseOutlined aria-label={t(`中断`)} className="shrink-0 text-gray-400" />
    }
    default: {
      return null
    }
  }
}

// 行の頭。開く前の一行は、ツールの行と束の見出しで同じ形にする。Safari は display を変えても既定の三角を出すので、疑似要素ごと消す
const TOOL_SUMMARY_CLASS =
  'flex cursor-pointer list-none items-center gap-1.5 hover:text-gray-700 [&::-webkit-details-marker]:hidden'

/**
 * MCP のツールの呼び出しの行。「何をしているか」の一行をたたんで出し、開くと呼び出し内容（ツール名と引数）を出す
 * （Lab の ADR-0002 の 2026-08-20 の改訂の①）。結果（detail）は、メールアドレスなどの個人情報がそのまま出るので出さない。
 *
 * 引数には個人情報が入りうるが、伏せる仕組み（XXX-12）ができるまでは伏せずに出すことを受け入れた
 * （#10183。!7819 の note_4113480738 で生の出力の欄を外した後に PdM と決めた）。
 * 承認のモード（I-3、C-8）で、何を承認するかを判断する材料に要るためである。出るのは本人が開いたときの本人の画面だけで、
 * 引数は会話記録にすでに残っている。XXX-12 で伏せる仕組みができたら、ここで出す引数にも同じ伏せ方を当てる
 */
const ToolItemView = ({
  item,
  activity,
}: {
  item: Extract<ChatItem, { kind: 'tool' }>
  activity: ToolActivity
}): ReactElement => {
  const { t } = useTranslation()
  const label = toolActivityLabelOf(activity, toolActivityLabelsOf(t))
  return (
    <details data-e2e="agent-chat-tool" className="min-w-0 text-xs text-gray-500">
      <summary className={TOOL_SUMMARY_CLASS}>
        {/* 飾りのアイコン。antd の既定のラベル（tool）を読み上げないようにする */}
        <ToolOutlined aria-hidden className="shrink-0" />
        <span className="truncate">{label}</span>
        <ToolEndMark status={item.status} />
      </summary>
      <div className="ml-4 mt-1 rounded bg-gray-50 p-2 text-gray-700" data-e2e="agent-chat-tool-call">
        <code className="break-all">{item.title}</code>
        {item.toolArguments !== null && (
          <pre className="m-0 mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all text-[11px]">
            {item.toolArguments}
          </pre>
        )}
      </div>
    </details>
  )
}

type ToolGroupRow = Extract<ChatRow, { kind: 'toolGroup' }>

/**
 * 同じツールが続いた呼び出しの束。見出しは対象の名前を入れない一行と回数で、開くと呼び出しごとの行が並ぶ。
 * 1 つのターンで同じツールを何度も呼ぶと、行が並んで回答が下に押し出されるためである
 */
const AgentToolGroupView = ({ group }: { group: ToolGroupRow }): ReactElement => {
  const { t } = useTranslation()
  const label = toolActivityLabelOf(group.activity, toolActivityLabelsOf(t))
  return (
    <details data-e2e="agent-chat-tool-group" className="min-w-0 pl-8 text-xs text-gray-500">
      <summary className={TOOL_SUMMARY_CLASS}>
        <ToolOutlined aria-hidden className="shrink-0" />
        <span className="truncate">{label}</span>
        <span className="shrink-0 rounded bg-gray-100 px-1 text-gray-600">
          {t(`{{count}} 回`, { count: group.tools.length })}
        </span>
        <ToolEndMark status={group.status} />
      </summary>
      <div className="ml-4 mt-1 flex flex-col gap-1">
        {group.tools.map((tool) => (
          <ToolItemView key={tool.id} item={tool} activity={tool.activity} />
        ))}
      </div>
    </details>
  )
}

// 束は描くたびに作り直されるので、中の呼び出しが変わらなければ、回答の増分で描き直さない。
// 見出しと終わり方の印は中の呼び出しから決まるので、呼び出しの参照だけを比べる
const isSameToolGroup = (previous: { group: ToolGroupRow }, next: { group: ToolGroupRow }): boolean =>
  previous.group.tools.length === next.group.tools.length &&
  previous.group.tools.every((tool, index) => tool === next.group.tools.at(index))

export const AgentToolGroup = memo(AgentToolGroupView, isSameToolGroup)

/**
 * ツールの実行の前の承認のカード。Lab（salescore-agent-lab の PR #11）と同じく、何をするかを「確認：…」の一行で示し、
 * ツール名と引数、コマンドの文は詳細にたたむ。利用者に見せるのはツール名ではなく何をしているかで、ツールの行と同じ言い方にする。
 * 送っている間は、会話の印（isAnswering）でボタンを押せなくする（閉じて開き直したカードでも同じ）
 */
const ApprovalItemView = ({ item }: { item: Extract<ChatItem, { kind: 'approval' }> }): ReactElement => {
  const { t } = useTranslation()
  const { answerApproval } = useAgentChatActions()
  const answer = (decision: ApprovalDecision): void => {
    answerApproval(item.turnId, item.id, decision)
  }
  // MCP でない承認（コマンドの実行、ファイルの編集）は、ツールの行には出さないので、一行は種類だけで決める
  const nonMcpActionLabel = item.approvalType === 'fileChange' ? t(`ファイルを編集`) : t(`コマンドを実行`)
  const actionLabel =
    item.activity === null ? nonMcpActionLabel : toolActivityLabelOf(item.activity, toolActivityLabelsOf(t))
  const outcomeLabels = {
    accept: t(`承認しました`),
    decline: t(`反映しませんでした`),
    cancelled: t(`答えないまま終わったため、反映しませんでした`),
    notAccepted: t(`この承認はもう受け付けられません`),
  } as const
  const hasCall = item.tool !== null || item.command !== null

  return (
    <div className="pl-8" data-e2e="agent-chat-approval">
      <div
        className={
          item.answer === null
            ? 'flex items-start gap-2 rounded-lg border border-solid border-amber-300 bg-amber-50 px-3 py-2'
            : 'flex items-start gap-2 rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-2 text-gray-500'
        }
      >
        <SafetyOutlined
          aria-hidden
          className={item.answer === null ? 'mt-1 shrink-0 text-amber-600' : 'mt-1 shrink-0'}
        />
        <div className="min-w-0 flex-1">
          <div className="break-words">
            <span>{t(`確認：`)}</span>
            {actionLabel}
          </div>
          {item.reason !== null && <div className="mt-1 break-words text-xs text-gray-500">{item.reason}</div>}
          {item.answer === null ? (
            <div className="mt-1 text-xs text-gray-500">{t(`承認するまで反映されません。`)}</div>
          ) : (
            <div className="mt-1 flex items-center gap-1 text-xs" data-e2e="agent-chat-approval-outcome">
              {item.answer === 'accept' ? <CheckOutlined aria-hidden /> : <CloseOutlined aria-hidden />}
              {outcomeLabels[item.answer]}
            </div>
          )}
          {hasCall && (
            <details className="mt-1 text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">{t(`詳細`)}</summary>
              <div className="mt-1 rounded bg-white p-2 text-gray-700">
                {item.tool !== null && <code className="break-all">{item.tool}</code>}
                {item.toolArguments !== null && (
                  <pre className="m-0 mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all text-[11px]">
                    {item.toolArguments}
                  </pre>
                )}
                {item.command !== null && (
                  <pre className="m-0 whitespace-pre-wrap break-all text-[11px]">{item.command}</pre>
                )}
              </div>
            </details>
          )}
          {item.answer === null && (
            // 入力欄にフォーカスがあるときに押しても、フォーカスを入力欄に残す（回答の中のリンクと同じ）
            <div className="mt-2 flex gap-1.5">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={item.isAnswering}
                disabled={item.isAnswering}
                onMouseDown={keepFocusOnMouseDown}
                onClick={() => {
                  answer('accept')
                }}
              >
                {t(`承認`)}
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                disabled={item.isAnswering}
                onMouseDown={keepFocusOnMouseDown}
                onClick={() => {
                  answer('decline')
                }}
              >
                {t(`却下`)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const TurnEndItemView = ({ item }: { item: Extract<ChatItem, { kind: 'turnEnd' }> }): ReactElement => {
  const { t } = useTranslation()
  return (
    <div data-e2e="agent-chat-turn-end" className="pl-8">
      <Alert
        type={item.status === 'interrupted' ? 'warning' : 'error'}
        showIcon
        message={item.status === 'interrupted' ? t(`中断しました`) : t(`失敗しました`)}
        description={item.message}
      />
    </div>
  )
}

const AgentChatItemView = ({ item }: { item: ChatItem }): ReactElement | null => {
  switch (item.kind) {
    case 'user': {
      return (
        <div className="flex justify-end">
          <div
            data-e2e="agent-chat-user"
            className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-blue-500 px-3 py-2 text-white"
          >
            {item.text}
          </div>
        </div>
      )
    }
    case 'assistant': {
      // 本文に kpi-plan のブロックがあれば、本文から外してプランのカードで出す（仕様の「プラン単位の承認」）
      const { plan, text } = splitKpiPlan(item.text)
      return (
        <div className="flex gap-2">
          <AgentAvatar />
          <div data-e2e="agent-chat-answer" className="min-w-0 flex-1 break-words leading-relaxed">
            <AgentMarkdown text={text} isStreaming={item.isStreaming} />
            {plan !== null && <KpiPlanCard key={plan.planId} plan={plan} />}
          </div>
        </div>
      )
    }
    // コマンドの実行とファイルの編集（activity が null）は会話に出さない（toolActivity.ts）
    case 'tool': {
      return item.activity === null ? null : (
        <div className="pl-8">
          <ToolItemView item={item} activity={item.activity} />
        </div>
      )
    }
    case 'approval': {
      return <ApprovalItemView item={item} />
    }
    case 'turnEnd': {
      return <TurnEndItemView item={item} />
    }
  }
}

// 変わらない行の参照を保つ domain の状態更新と組み合わせ、回答の増分で既存の行を再描画しない。
export const AgentChatItem = memo(AgentChatItemView)
